import {
  TransactionStatus,
  TransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  toWSDate,
  toWSMoneyString,
  useModalContext,
  useWSSnackbar,
  WSBadge,
  WSButton,
  WSButtons,
  WSDateInput,
  WSDateRangeSelectOptions,
  WSElement,
  WSFilters,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSIcon,
  WSInfiniteScroll,
  WSPage,
  WSPanel,
  WSRadioInputGroup,
  WSTable,
  WSTableColumn,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "accounting";
import classNames from "classnames";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import reduce from "lodash/reduce";
import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { DefaultBookkeepingFilters } from "../../Bookkeeping/screens/dashboard/BookkeepingIndex";
import styles from "../../Bookkeeping/screens/dashboard/BookkeepingTransactions.module.scss";
import {
  AmountColumnProps,
  createTransactionParams
} from "../../Bookkeeping/screens/dashboard/BookkeepingTransactionsV2";
import {
  buildPropsIconByTransaction,
  getAmountValueAndFormat,
  getTransactionName
} from "../../Bookkeeping/utils";
import {
  TransactionParams,
  useBankingAccount,
  useBankingBalance,
  useCashbackEligibility,
  usePostedCashbackBalance,
  useTransactions
} from "../../query/bookkeeping/queries";
import { useCardsQuery, usePayoutSettings } from "../../query/payments/queries";
import { WSQueries } from "../../query/WSQuery";
import { useUrlQueryFilters } from "../../utils/router";
import { BankCardOTPModal } from "../components/BankCard/BankCardOTPModal";
import { BankCardsList } from "../components/BankCardsList/BankCardsList";
import { WalletTransaction } from "./WalletTransaction";
import {
  AccountDetailsModal,
  WALLET_ACCOUNT_DETAILS
} from "../modals/AccountDetails";
import {
  WITHDRAW_FUNDS_MODAL,
  WithdrawFundsModal
} from "../modals/WithdrawFunds";
import { ADD_FUNDS_MODAL, AddFundsModal } from "../modals/AddFunds";
import { PendingTransactionsList } from "./PendingTransactionsList";
import { useUserId } from "../../query/hooks/helpers";
import {
  UPDATE_DEPOSIT_RATE_MODAL,
  UpdateDepositRateModal
} from "../modals/UpdateDepositRate";
import { useUpdateActivity } from "../../query/users/mutations";
import { useActivities } from "../../query/users/queries";
import { useRedeemCashbackBalance } from "../../query/bookkeeping/mutations";

export const AmountColumn: React.FC<AmountColumnProps> = ({ transaction }) =>
  transaction ? (
    <>
      <WSText
        align="right"
        {...getAmountValueAndFormat(transaction)}
        {...(transaction?.amount < 0
          ? {
              color: "green500"
            }
          : {})}
      />
      {transaction?.labels?.rewardTransactionId ? (
        <WSText mt="XS" color="green500">
          +{toWSMoneyString(Math.abs(transaction.amount) * 0.01)} cashback
        </WSText>
      ) : null}
    </>
  ) : (
    <WSText>0</WSText>
  );

const columns: Array<WSTableColumn> = [
  {
    config: {
      header: { text: "Merchant" },
      gridTemplateSizeMax: "0.8fr"
    },
    renderFunction: rowData => (
      <>
        <WSFlexBox.CenterY wrap="nowrap">
          <WSText.Paragraph
            color={rowData.data.business ? "grey800" : "grey600"}
            singleLine
            data-testId="transactionMerchant"
          >
            {rowData.data.merchant}
          </WSText.Paragraph>
          {rowData.data.business ? null : (
            <WSBadge ml="S" textProps={{ kind: "Heading6" }} text="Ignored" />
          )}
        </WSFlexBox.CenterY>
        <WSFlexBox.CenterY>
          <WSText.ParagraphSmall
            color={
              rowData.data.business &&
              rowData.data.type !== TransactionType.Transfer
                ? "grey700"
                : "grey500"
            }
            singleLine
            formatDate="monthDayYear"
          >
            {rowData.data.date}
          </WSText.ParagraphSmall>
          {rowData.data.business && rowData.data.receipt ? (
            <WSIcon ml="S" name="receipt" size="S" color="grey500" />
          ) : null}
        </WSFlexBox.CenterY>
      </>
    )
  },
  {
    config: {
      hideOnScreens: ["XS", "S", "M"],
      header: { text: "Category" },
      justify: "end"
    },
    renderFunction: rowData => (
      <WSText.Paragraph
        singleLine
        color={
          rowData.data.business &&
          rowData.data.type !== TransactionType.Transfer
            ? "grey800"
            : "grey600"
        }
      >
        {rowData.data.category
          ? `${rowData.data.category}${
              rowData.data.subcategory ? ` - ${rowData.data.subcategory}` : ""
            }`
          : rowData.data.type}
      </WSText.Paragraph>
    )
  },
  {
    config: {
      hideOnScreens: ["XS"],
      header: { text: "Account" },
      justify: "end"
    },
    renderFunction: rowData => (
      <WSText.Paragraph
        singleLine
        color={
          rowData.data.business &&
          rowData.data.type !== TransactionType.Transfer
            ? "grey800"
            : "grey600"
        }
      >
        {rowData.data.account ? `**** ${rowData.data.account}` : "-"}
      </WSText.Paragraph>
    )
  },
  {
    config: {
      justify: "end",
      header: { text: "Amount" }
    },
    renderFunction: rowData => (
      <AmountColumn transaction={rowData.data.source} />
    )
  }
];

export type DefaultWalletFilters = TransactionParams & { search: string };

export const defaultWalletFilters: DefaultWalletFilters = {
  ...createTransactionParams(),
  status: [TransactionStatus.Active, TransactionStatus.Inactive],
  type: [
    TransactionType.Expense,
    TransactionType.Income,
    TransactionType.Transfer,
    // TransactionType.Reward,
    TransactionType.Unknown
  ],
  nonBusiness: true,
  businessBanking: true,
  search: ""
};

export const reversePercent = (value?: number) => {
  return 100 - (value || 0);
};

export const useWalletFilters = () =>
  useUrlQueryFilters<DefaultBookkeepingFilters>(defaultWalletFilters);

export const WalletDashboard: React.FC = () => {
  const {
    defaultFilters,
    filters,
    setFilters,
    redirectWithFilters
  } = useWalletFilters();

  const { openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();

  const { search, ...params } = filters;
  const userId = useUserId();
  const qCardsQuery = useCardsQuery();
  const qBankingBalance = useBankingBalance();
  const qBankingAccount = useBankingAccount();
  const qPayoutSettings = usePayoutSettings(userId);
  const qTransactions = useTransactions(filters);

  const qCashbackEligibility = useCashbackEligibility(userId);
  const qPostedCashbackBalance = usePostedCashbackBalance(userId, {
    enabled: qCashbackEligibility.data?.isUserEligibleForCashback
  });

  const [
    redeemCashbackBalance,
    redeemCashbackBalanceMeta
  ] = useRedeemCashbackBalance();

  const [updateActivity] = useUpdateActivity(userId);

  useActivities(userId, {
    onSuccess(data) {
      if (!data.flows.walletSetup?.complete) {
        updateActivity({
          flows: {
            walletSetup: {
              complete: true,
              version: 2,
              currentStep: 1,
              totalSteps: 2
            }
          }
        });
      }
    }
  });

  const areDefaultFilters = isEqual(defaultFilters, params);

  return (
    <WSQueries
      queries={{
        qCardsQuery,
        qBankingBalance,
        qBankingAccount,
        qPayoutSettings
      }}
    >
      {({ qCardsQuery, qBankingBalance, qBankingAccount, qPayoutSettings }) => {
        const transactionsList = flatten(qTransactions.data || []);

        const transactionsListMapped = transactionsList.map(transaction => {
          const card = qCardsQuery.data?.find(
            card => card.cardId === transaction.wingspanCardId
          );

          return {
            icon: buildPropsIconByTransaction(transaction),
            data: {
              source: transaction,
              merchant: getTransactionName(transaction),
              date: transaction.date,
              type: transaction.type,
              amount: transaction.amount,
              receipt: transaction.receiptFileId,
              business: true,
              category: transaction.wsCategory,
              subcategory: transaction.labels.subcategory,
              account: card?.last4Digits
            },
            id: transaction.transactionId
          };
        });

        const depositRatePercentage =
          qPayoutSettings.data.payoutDestinations.find(p =>
            Number.isInteger(p.percentage)
          )?.percentage || 0;

        const cashbackBalance =
          qPostedCashbackBalance.data?.postedCashbackBalance || 0;

        return (
          <WSPage title="Wingspan Wallet">
            <AccountDetailsModal />
            <AddFundsModal />
            <WithdrawFundsModal />
            <UpdateDepositRateModal />

            <WSGrid.Container data-testid="walletDashboard" strictHeight>
              <WSGrid.Item L={4} M={6}>
                <WSPanel m="NONE">
                  <WSFlexBox.CenterY justify="space-between">
                    <WSText.Heading4 py="XS">Available balance</WSText.Heading4>

                    {qBankingAccount.data.numbers.account ? (
                      <WSButton.Link
                        inline
                        my="XS"
                        name="viewRoutingNumber"
                        onClick={() => openModal(WALLET_ACCOUNT_DETAILS)}
                      >
                        {`(...${qBankingAccount.data.numbers.account.slice(
                          qBankingAccount.data.numbers.account.length - 4
                        )})→`}
                      </WSButton.Link>
                    ) : null}
                  </WSFlexBox.CenterY>
                  <WSText.Heading1 my="XS" singleLine>
                    {formatMoney(qBankingBalance.data.balance)}
                  </WSText.Heading1>
                  <WSText.LabelSmall color="grey500">&nbsp;</WSText.LabelSmall>
                  <WSButtons mt="M">
                    <WSButton.Secondary
                      small
                      icon="addFunds"
                      name="addFunds"
                      onClick={() => openModal(ADD_FUNDS_MODAL)}
                      style={{ flex: 1 }}
                    >
                      Add funds
                    </WSButton.Secondary>
                    <WSButton.Secondary
                      small
                      onClick={() => openModal(WITHDRAW_FUNDS_MODAL)}
                      icon="withdrawFunds"
                      name="withdrawFunds"
                      style={{ flex: 1 }}
                    >
                      Withdraw funds
                    </WSButton.Secondary>
                  </WSButtons>
                </WSPanel>
              </WSGrid.Item>

              <WSGrid.Item L={4} M={6}>
                <WSPanel m="NONE">
                  <WSText.Heading4 py="XS">Cashback balance</WSText.Heading4>
                  <WSText.Heading1
                    my="XS"
                    color={cashbackBalance > 1 ? "green500" : "grey400"}
                  >
                    {formatMoney(cashbackBalance)}
                  </WSText.Heading1>
                  <WSText.LabelSmall color="grey500">&nbsp;</WSText.LabelSmall>
                  <WSButton
                    kind={cashbackBalance > 1 ? "Secondary" : "Tertiary"}
                    small
                    data-testid="redeemCashback"
                    icon="linkedAccounts"
                    fullWidth
                    mt="M"
                    disabled={cashbackBalance < 1}
                    onAsyncClick={async () => {
                      await redeemCashbackBalance(
                        {},
                        {
                          onSuccess() {
                            openSnackbar({
                              type: "success",
                              message: "Cashback successfully sent!"
                            });
                          },
                          onError() {
                            openSnackbar({
                              type: "warning",
                              message: "Failed to send cashback!"
                            });
                          }
                        }
                      );
                    }}
                  >
                    Redeem
                  </WSButton>
                </WSPanel>
              </WSGrid.Item>
              {Number.isInteger(depositRatePercentage) ? (
                <WSGrid.Item L={4} M={6}>
                  <WSPanel m="NONE">
                    <WSText.Heading4 py="XS">Deposit rate</WSText.Heading4>
                    <WSText.Heading1 my="XS" data-testid="depositRate">
                      {reversePercent(depositRatePercentage)}%
                    </WSText.Heading1>
                    <WSText.LabelSmall color="grey500">
                      of every product
                    </WSText.LabelSmall>
                    <WSButton.Secondary
                      small
                      mt="M"
                      fullWidth
                      name="updateDepositRate"
                      icon="interest"
                      onClick={() => openModal(UPDATE_DEPOSIT_RATE_MODAL)}
                    >
                      Change Deposit Rate
                    </WSButton.Secondary>
                  </WSPanel>
                </WSGrid.Item>
              ) : null}
            </WSGrid.Container>

            <BankCardsList basePath="/member/wallet/dashboard/cards" />

            <PendingTransactionsList />

            <WSText.Heading2 my="L">Transactions</WSText.Heading2>
            <WSFilters
              className={classNames(styles.filters, styles.sticky)}
              getCount={(defaultValues, currentValues) => {
                const _defaultValues = omit(defaultValues, [
                  "search",
                  "customDateRange"
                ]);
                const _currentValues = omit(currentValues, [
                  "search",
                  "customDateRange"
                ]);

                return reduce<any, any>(
                  _currentValues,
                  (result, value, key) =>
                    isEqual(value, _defaultValues[key])
                      ? result
                      : result.concat(key),
                  []
                ).length;
              }}
              filters={params}
              searchQuery={search}
              defaultFilters={defaultFilters}
              onFilter={(newParams: TransactionParams) => {
                let custom = {};

                if (
                  newParams.dateRange !== filters.dateRange &&
                  newParams.dateRange !== "CUSTOM"
                ) {
                  custom = {
                    customDateRange:
                      newParams.dateRange === "PAST_WEEK"
                        ? WSDateRangeSelectOptions.PastWeek.range
                        : newParams.dateRange === "PAST_MONTH"
                        ? WSDateRangeSelectOptions.PastMonth.range
                        : newParams.dateRange === "PAST_YEAR"
                        ? WSDateRangeSelectOptions.Past12Months.range
                        : []
                  };
                }

                if (newParams.category !== "CUSTOM") {
                  newParams.wsCategory = undefined;
                  newParams.type = undefined;
                  newParams.subcategory = undefined;
                  newParams.nonBusiness = undefined;
                }

                setFilters({
                  ...filters,
                  ...(newParams as TransactionParams),
                  ...custom
                });
              }}
              onSearch={newSearch =>
                setFilters({
                  ...filters,
                  search: newSearch
                })
              }
            >
              <WSForm.Field
                name="dateRange"
                label="Date range"
                mb="M"
                component={WSRadioInputGroup}
                componentProps={{
                  options: [
                    {
                      label: "All time",
                      value: "ALL"
                    },
                    {
                      label: "Past week",
                      value: "PAST_WEEK"
                    },
                    {
                      label: "Past month",
                      value: "PAST_MONTH"
                    },
                    {
                      label: "Past year",
                      value: "PAST_YEAR"
                    },
                    {
                      label: (
                        <WSForm.Context>
                          {({ setValue }) => (
                            <WSFlexBox
                              alignItems="center"
                              className={styles.dateRange}
                            >
                              <WSText mr="S">From</WSText>
                              <WSForm.Field
                                name="customDateRange"
                                component={WSDateInput}
                                componentProps={{
                                  range: true,
                                  placeholder: "Select date range",
                                  onFocus() {
                                    setValue("dateRange", "CUSTOM");
                                  }
                                }}
                              />
                            </WSFlexBox>
                          )}
                        </WSForm.Context>
                      ),
                      value: "CUSTOM"
                    }
                  ]
                }}
              />

              {/*<WSForm.Field*/}
              {/*  name="category"*/}
              {/*  label="Category"*/}
              {/*  mb="M"*/}
              {/*  component={WSRadioInputGroup}*/}
              {/*  componentProps={{*/}
              {/*    options: [*/}
              {/*      {*/}
              {/*        label: "All",*/}
              {/*        value: "ALL"*/}
              {/*      },*/}
              {/*      {*/}
              {/*        label: (*/}
              {/*          <WSForm.Context>*/}
              {/*            {({ setValue }) => (*/}
              {/*              <WSForm.Values*/}
              {/*                names={[*/}
              {/*                  "wsCategory",*/}
              {/*                  "subcategory",*/}
              {/*                  "type",*/}
              {/*                  "nonBusiness",*/}
              {/*                  "isPositive"*/}
              {/*                ]}*/}
              {/*              >*/}
              {/*                {({*/}
              {/*                  wsCategory,*/}
              {/*                  subcategory,*/}
              {/*                  type,*/}
              {/*                  isPositive,*/}
              {/*                  nonBusiness*/}
              {/*                }) => (*/}
              {/*                  <>*/}
              {/*                    <WSCategories*/}
              {/*                      header={"Filter by category"}*/}
              {/*                      isSelectOnly={true}*/}
              {/*                      data={{*/}
              {/*                        type,*/}
              {/*                        business: !nonBusiness,*/}
              {/*                        isPositive: true,*/}
              {/*                        wsCategory,*/}
              {/*                        subcategory*/}
              {/*                      }}*/}
              {/*                      renderTrigger={({ label, value, open }) => {*/}
              {/*                        if (*/}
              {/*                          label.wsCategory ||*/}
              {/*                          label.pseudoCategory*/}
              {/*                        ) {*/}
              {/*                          return (*/}
              {/*                            <WSFlexBox.CenterY*/}
              {/*                              wrap="nowrap"*/}
              {/*                              justify="space-between"*/}
              {/*                              className={styles.selectCategory}*/}
              {/*                              onClick={open}*/}
              {/*                              data-testid="categoryLabel"*/}
              {/*                            >*/}
              {/*                              <WSFlexBox.CenterY>*/}
              {/*                                <WSCategoryIcon*/}
              {/*                                  value={value}*/}
              {/*                                  mr="S"*/}
              {/*                                  size="M"*/}
              {/*                                />*/}
              {/*                                <WSElement>*/}
              {/*                                  <WSText.Paragraph color="grey700">*/}
              {/*                                    {label.pseudoCategory ??*/}
              {/*                                      label.wsCategory}*/}
              {/*                                  </WSText.Paragraph>*/}
              {/*                                  <WSText.Label color="grey600">*/}
              {/*                                    {label.pseudoCategory*/}
              {/*                                      ? label.wsCategory*/}
              {/*                                      : label.subcategory}*/}
              {/*                                  </WSText.Label>*/}
              {/*                                </WSElement>*/}
              {/*                              </WSFlexBox.CenterY>*/}
              {/*                              <WSIcon*/}
              {/*                                size="XS"*/}
              {/*                                name="chevronDown"*/}
              {/*                                color="blue500"*/}
              {/*                              />*/}
              {/*                            </WSFlexBox.CenterY>*/}
              {/*                          );*/}
              {/*                        }*/}

              {/*                        return (*/}
              {/*                          <WSFlexBox.CenterY*/}
              {/*                            wrap="nowrap"*/}
              {/*                            justify="space-between"*/}
              {/*                            className={styles.selectCategory}*/}
              {/*                            onClick={open}*/}
              {/*                            data-testid="addCategory"*/}
              {/*                          >*/}
              {/*                            <WSFlexBox.CenterY>*/}
              {/*                              <WSText.Paragraph color="blue500">*/}
              {/*                                Add category*/}
              {/*                              </WSText.Paragraph>*/}
              {/*                            </WSFlexBox.CenterY>*/}
              {/*                            <WSIcon*/}
              {/*                              size="XS"*/}
              {/*                              name="chevronDown"*/}
              {/*                              color="blue500"*/}
              {/*                            />*/}
              {/*                          </WSFlexBox.CenterY>*/}
              {/*                        );*/}
              {/*                      }}*/}
              {/*                      onUpdate={async data => {*/}
              {/*                        setValue("category", "CUSTOM");*/}
              {/*                        setValue("wsCategory", data.wsCategory);*/}
              {/*                        setValue("subcategory", data.subcategory);*/}
              {/*                        setValue("type", data.type);*/}
              {/*                        setValue("nonBusiness", !data.business);*/}
              {/*                      }}*/}
              {/*                    />*/}
              {/*                  </>*/}
              {/*                )}*/}
              {/*              </WSForm.Values>*/}
              {/*            )}*/}
              {/*          </WSForm.Context>*/}
              {/*        ),*/}
              {/*        value: "CUSTOM"*/}
              {/*      }*/}
              {/*    ]*/}
              {/*  }}*/}
              {/*/>*/}

              <WSForm.Field
                hidden={true}
                label="Type"
                name="type"
                component={WSTextInput}
              />
              <WSForm.Field
                hidden={true}
                name="wsCategory"
                component={WSTextInput}
              />
              <WSForm.Field
                hidden={true}
                name="subcategory"
                component={WSTextInput}
              />
              <WSForm.Field
                hidden={true}
                name="nonBusiness"
                component={WSTextInput}
              />
              <WSForm.Field
                hidden={true}
                name="isPositive"
                component={WSTextInput}
              />
            </WSFilters>
            <WSElement py="M" />
            <WSInfiniteScroll
              onLoad={() => {
                qTransactions.fetchMore();
              }}
              loadMore={transactionsList.length > 0}
              endOfList={!qTransactions.canFetchMore}
              loading={!!qTransactions.isFetchingMore}
            >
              <WSTable
                showHeader={false}
                mb="S"
                mt={
                  qTransactions.isLoading === false &&
                  transactionsList.length === 0
                    ? "L"
                    : "-M"
                }
                showIcon
                loading={qTransactions.isLoading}
                columns={columns}
                onRowClick={item => {
                  redirectWithFilters(
                    `/member/wallet/dashboard/transactions/${item.data.source.transactionId}`
                  );
                }}
                getGroupLabel={item => {
                  const year = new Date(item.data.date).getFullYear();
                  const currentYear = new Date().getFullYear();

                  const month = toWSDate(item.data.date, "month").toString();

                  if (year === currentYear) {
                    return month;
                  }

                  return `${month} ${year}`;
                }}
                tableData={transactionsListMapped}
                bottomMessage={
                  qTransactions.isLoading === false &&
                  transactionsList.length === 0 &&
                  (!areDefaultFilters || search ? (
                    <>
                      No transactions found.{" "}
                      <WSText.Heading5
                        color="blue500"
                        inline
                        onClick={() => setFilters(defaultFilters)}
                      >
                        Show full transaction history.
                      </WSText.Heading5>
                    </>
                  ) : (
                    "No transactions for this date range."
                  ))
                }
              />
            </WSInfiniteScroll>

            <Switch>
              <Route
                path="/member/wallet/dashboard/transactions/:transactionId"
                exact
                component={WalletTransaction}
              />

              <Redirect from="*" exact to="/member/wallet/dashboard" />
            </Switch>

            <BankCardOTPModal />
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
